import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    MapPin,
    Gauge,
    BadgeCheck,
    ChevronLeft,
    ChevronRight,
    Zap,
    Star,
    Wrench,
    Eye,
} from "lucide-react";
import { motion } from "framer-motion";
import { api, absUrl, formatApiError } from "@/lib/api";
import { formatMileage, timeAgo } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import VehicleVoteButton from "@/components/VehicleVoteButton";

export default function VehicleDetail() {
    const { id } = useParams();
    const [vehicle, setVehicle] = useState(null);
    const [owner, setOwner] = useState(null);
    const [idx, setIdx] = useState(0);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/vehicles/${id}`);
                setVehicle(data);
                if (data?.owner_id) {
                    try {
                        const { data: u } = await api.get(`/users/${data.owner_id}`);
                        setOwner(u);
                    } catch {}
                }
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [id]);

    if (!vehicle) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-onyx-900">
                <div className="w-10 h-10 border-2 border-onyx-600 border-t-hellcat rounded-full animate-spin" />
            </div>
        );
    }

    const photos = vehicle.photos?.length ? vehicle.photos : [];
    const isMine = user && vehicle.owner_id === user.user_id;
    const seoTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model_name} ${vehicle.trim} — SRT Finder Garage`;
    const seoDesc = `${vehicle.year} ${vehicle.make} ${vehicle.model_name} ${vehicle.trim} (${vehicle.engine}, ${vehicle.drivetrain}, ${vehicle.transmission}) in ${owner?.name || "an SRT Finder member"}'s Garage. ${(vehicle.description || "").slice(0, 160)}`;

    return (
        <div className="min-h-screen bg-onyx-900">
            <Seo
                title={seoTitle}
                description={seoDesc}
                canonicalPath={`/vehicles/${vehicle.vehicle_id}`}
                image={photos[0]}
                imageAlt={seoTitle}
                type="article"
            />

            {/* Top bar */}
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-onyx-500 label-caps flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {isMine ? "Public preview" : "Vehicle"}
                        </div>
                        <div
                            className="text-base truncate"
                            style={{
                                fontFamily: "'DM Serif Display', serif",
                                fontWeight: 400,
                            }}
                        >
                            {vehicle.year} {vehicle.model_name} {vehicle.trim}
                        </div>
                    </div>
                    {isMine && (
                        <button
                            onClick={() => navigate(`/garage/vehicles/${vehicle.vehicle_id}`)}
                            className="btn-ghost px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5"
                            data-testid="edit-vehicle-btn"
                        >
                            <Wrench className="w-3.5 h-3.5" /> Edit
                        </button>
                    )}
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-5">
                {/* Gallery */}
                <div className="surface overflow-hidden">
                    <div
                        className="relative w-full listing-image"
                        style={{ aspectRatio: "16/10", borderRadius: "20px 20px 0 0" }}
                    >
                        {photos.length ? (
                            <img
                                key={idx}
                                src={absUrl(photos[idx])}
                                alt={`${vehicle.year} ${vehicle.model_name} ${vehicle.trim}`}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-onyx-500 label-caps">
                                No Photos
                            </div>
                        )}
                        {photos.length > 1 && (
                            <>
                                <button
                                    onClick={() => setIdx((idx - 1 + photos.length) % photos.length)}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                                    style={{ background: "rgba(10,10,10,0.7)" }}
                                    data-testid="gallery-prev"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setIdx((idx + 1) % photos.length)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                                    style={{ background: "rgba(10,10,10,0.7)" }}
                                    data-testid="gallery-next"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                    {photos.map((_, i) => (
                                        <span
                                            key={i}
                                            className="w-1.5 h-1.5 rounded-full"
                                            style={{
                                                background:
                                                    i === idx ? "#E10600" : "rgba(255,255,255,0.4)",
                                            }}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                        {vehicle.for_sale && (
                            <span
                                className="absolute top-3 left-3 rounded-full px-3 py-1 text-[10px] font-bold uppercase"
                                style={{
                                    background: "#E10600",
                                    color: "#fff",
                                    letterSpacing: "0.14em",
                                    boxShadow: "0 6px 18px rgba(225,6,0,0.35)",
                                }}
                            >
                                For Sale
                            </span>
                        )}
                    </div>
                    {photos.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto no-scrollbar p-2">
                            {photos.map((p, i) => (
                                <button
                                    key={p + i}
                                    onClick={() => setIdx(i)}
                                    className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 ${
                                        i === idx ? "border-hellcat" : "border-transparent"
                                    }`}
                                >
                                    <img
                                        src={absUrl(p)}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Title */}
                <div className="mt-5">
                    <div className="label-caps text-hellcat flex items-center justify-between gap-2">
                        <span>{vehicle.trim}</span>
                        {!isMine && (
                            <VehicleVoteButton vehicleId={vehicle.vehicle_id} />
                        )}
                    </div>
                    <h1
                        className="mt-1 text-white text-3xl sm:text-4xl"
                        style={{
                            fontFamily: "'DM Serif Display', serif",
                            fontWeight: 400,
                            letterSpacing: "-0.005em",
                        }}
                    >
                        {vehicle.year} {vehicle.make} {vehicle.model_name}
                    </h1>
                </div>

                {/* Quick facts */}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Fact label="Engine" value={vehicle.engine} icon={Zap} />
                    <Fact label="Drivetrain" value={vehicle.drivetrain} />
                    <Fact label="Trans" value={vehicle.transmission} />
                    <Fact label="Mods" value={vehicle.mods_level} />
                </div>

                {/* Description */}
                {vehicle.description && (
                    <section className="mt-6">
                        <div className="label-caps mb-2">About this build</div>
                        <p className="text-sm leading-relaxed text-onyx-300 whitespace-pre-wrap">
                            {vehicle.description}
                        </p>
                    </section>
                )}

                {vehicle.mods?.length > 0 && (
                    <section className="mt-6">
                        <div className="label-caps mb-2">Mods list</div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {vehicle.mods.map((m, i) => (
                                <li
                                    key={i}
                                    className="surface rounded-full px-4 py-2 text-sm flex items-center gap-2"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-hellcat" />
                                    {m}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Owner card */}
                {owner && (
                    <section className="mt-8">
                        <Link
                            to={`/users/${owner.user_id}`}
                            className="surface p-5 flex items-center gap-4"
                            data-testid="owner-card-link"
                        >
                            <div className="w-14 h-14 rounded-full overflow-hidden surface-elevated">
                                {owner.avatar_url ? (
                                    <img
                                        src={absUrl(owner.avatar_url)}
                                        alt="owner"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-onyx-500 label-caps">
                                        {owner.name?.[0]}
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <div
                                    className="text-lg text-white flex items-center gap-2"
                                    style={{
                                        fontFamily: "'DM Serif Display', serif",
                                        fontWeight: 400,
                                    }}
                                >
                                    {owner.name}
                                    {owner.verified_seller && (
                                        <BadgeCheck className="w-4 h-4 text-hellcat" />
                                    )}
                                </div>
                                <div className="text-xs text-onyx-500 flex items-center gap-3 mt-0.5">
                                    {owner.location && (
                                        <span className="flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {owner.location}
                                        </span>
                                    )}
                                    {owner.seller_rating > 0 && (
                                        <span className="flex items-center gap-1">
                                            <Star className="w-3 h-3 fill-hellcat text-hellcat" />
                                            {owner.seller_rating.toFixed(1)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Link>
                    </section>
                )}
            </div>
        </div>
    );
}

function Fact({ label, value, icon: Icon }) {
    return (
        <div className="surface rounded-2xl p-3">
            <div className="label-caps text-[10px]">{label}</div>
            <div className="mt-1 text-sm text-white flex items-center gap-1.5 truncate">
                {Icon && <Icon className="w-3.5 h-3.5 text-onyx-500 shrink-0" />}
                <span className="truncate">{value}</span>
            </div>
        </div>
    );
}
